import { ChevronLeft } from 'lucide-react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import { ErrorPageContainer } from '@/components/error-handling/error-page-container';
import { AccessGuard } from '@/features/billing/components/access-guard';
import { Button } from '@/components/ui/button';
import { MainContentLoading } from '@/components/ui/main-content-loading';
import { SubscriptionRecoveryBanner } from '@/features/billing/components/subscription-recovery/subscription-recovery-banner';
import { FOCUSED_FEATURE_SHELL_VARIANT } from './types/focused-feature-shell-enums';

export interface FocusedFeatureShellProps {
  children: ReactNode;
  variant?: FOCUSED_FEATURE_SHELL_VARIANT;
}

export default function FocusedFeatureShell({
  children,
  variant = FOCUSED_FEATURE_SHELL_VARIANT.FEATURE,
}: FocusedFeatureShellProps) {
  const navigate = useNavigate();
  const showBack = variant === FOCUSED_FEATURE_SHELL_VARIANT.FEATURE;

  const handleBackClick = () => {
    void navigate('/');
  };

  return (
    <div
      data-slot="focused-feature-shell"
      data-variant={variant}
      className="flex h-dvh flex-col bg-background font-sans"
    >
      <SubscriptionRecoveryBanner />

      {showBack ? (
        <header className="flex h-14 shrink-0 items-center gap-1 border-b border-border-default px-3">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-11 w-11 shrink-0 text-content-secondary hover:text-content-primary"
            aria-label="Back to dashboard"
            onClick={handleBackClick}
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
        </header>
      ) : null}

      <main
        id="focused-feature-main"
        tabIndex={-1}
        className="min-h-0 flex-1 overflow-y-auto outline-none"
      >
        <AccessGuard>
          {/*
            Vertical rhythm only — SettingsPage owns horizontal gutters
            (px-4 / md:px-6) so content is not inset twice.
          */}
          <div data-slot="focused-feature-outlet" className="py-6 md:py-8">
            <ErrorPageContainer fallback={<MainContentLoading />}>
              {children}
            </ErrorPageContainer>
          </div>
        </AccessGuard>
      </main>
    </div>
  );
}
